import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { Box, Typography, Snackbar, InputBase, Badge, useMediaQuery, useTheme } from '@mui/material';
import {
  Thermostat,
  Opacity,
  Co2,
  Science,
  VolumeUp,
  Air,
  FilterAlt,
  Cloud,
  Grass,
  Memory,
  Star,
  StarBorder,
  WarningAmberRounded,
  Layers,
  EditNote,
  DescriptionOutlined,
  ShowChart,
  PlaceOutlined,
  Search,
  NotificationsNone,
  Menu,
} from '@mui/icons-material';
import { DeviceNameMapper } from '../utils/DeviceNameMapper';
import { useAuth } from '../contexts/AuthContext';
import { avatarGradient, fontStack } from './auth/authStyles';
import DeviceCurvesModal from './DeviceCurvesModal';

const C = {
  bg: '#1a1b2e',
  topbar: '#1E1E2F',
  card: '#2E2F45',
  input: '#25263B',
  border: '#3A3B55',
  textPrimary: '#FFFFFF',
  textMuted: '#A7AEC2',
  textPlaceholder: '#6D7593',
  danger: '#FF5722',
};

const TYPE_META = {
  climatrack: { color: '#34D399', tint: 'rgba(52,211,153,0.13)', border: 'rgba(5,150,105,0.55)' },
  aquacheck: { color: '#22D3EE', tint: 'rgba(34,211,238,0.13)', border: 'rgba(34,211,238,0.55)' },
  greenwatch: { color: '#F59E0B', tint: 'rgba(245,158,11,0.13)', border: 'rgba(245,158,11,0.55)' },
};

const ALL_TABLES = ['climatrack', 'aquacheck', 'greenwatch'];
const FILTER_OPTIONS = ['tous', ...ALL_TABLES];

const metric = (key, label, unit, Icon, thr) => ({ key, label, unit, Icon, thr });

const METRICS = {
  aquacheck: [
    metric('temperature', 'Temp', '°C', Thermostat, 'temperature'),
    metric('humidity', 'Humidité', '%', Opacity, 'humidity'),
    metric('ground_humidity', 'Humidité sol', '%', Grass, 'groundHumidity'),
  ],
  climatrack: [
    metric('temperature', 'Temp', '°C', Thermostat, 'temperature'),
    metric('humidity', 'Humidité', '%', Opacity, 'humidity'),
    metric('co2', 'CO2', 'ppm', Co2, 'CO2'),
    metric('tvoc', 'TVOC', 'ppb', Science, 'TVOC'),
    metric('sound_level', 'Son', 'dB', VolumeUp, 'sound'),
    metric('pm1_0', 'PM1.0', 'µg/m³', Air, 'PM1_0'),
    metric('pm2_5', 'PM2.5', 'µg/m³', FilterAlt, 'PM2_5'),
    metric('pm10', 'PM10', 'µg/m³', Cloud, 'PM10'),
  ],
  greenwatch: [
    metric('temperature', 'Temp', '°C', Thermostat, 'temperature'),
    metric('humidity', 'Humidité', '%', Opacity, 'humidity'),
    metric('ground_humidity', 'Humidité sol', '%', Grass, 'groundHumidity'),
    metric('lumiere', 'Lumière', 'lx', Cloud, 'lumiere'),
    metric('pluviometre', 'Pluie', 'mm', Opacity, 'pluviometre'),
  ],
};

const getMetrics = (table) => METRICS[table] || METRICS.climatrack;

const hasMetricValue = (v) => {
  const n = Number(v);
  return !(v === null || v === undefined || v === '' || Number.isNaN(n));
};

const formatValue = (v) => {
  const n = Number(v);
  if (!hasMetricValue(v)) return '---';
  return Math.abs(n) >= 100 ? String(Math.round(n)) : String(Math.round(n * 10) / 10);
};

const getMetricValue = (data, key) => {
  if (!data || typeof data !== 'object') return undefined;
  return data[key] ?? data[key.toLowerCase()] ?? data[key.toUpperCase()];
};

const hasAnyUsableMetric = (kpis) => {
  return kpis.some((kpi) => hasMetricValue(kpi.value));
};

const getVisibleKpis = (kpis) => {
  return kpis.filter((kpi) => hasMetricValue(kpi.value));
};

const getActivityStatus = (timestamp) => {
  if (!timestamp) {
    return {
      code: 'inactive',
      color: '#EF4444',
      label: 'Aucune activité connue',
    };
  }

  const lastDate = new Date(timestamp);

  if (Number.isNaN(lastDate.getTime())) {
    return {
      code: 'inactive',
      color: '#EF4444',
      label: 'Date d’activité invalide',
    };
  }

  const now = new Date();

  const isToday =
    lastDate.getFullYear() === now.getFullYear() &&
    lastDate.getMonth() === now.getMonth() &&
    lastDate.getDate() === now.getDate();

  if (isToday) {
    return {
      code: 'active',
      color: '#22C55E',
      label: 'Actif aujourd’hui',
    };
  }

  const TWO_DAYS_IN_MS = 2 * 24 * 60 * 60 * 1000;
  const timeSinceLastActivity = now.getTime() - lastDate.getTime();

  if (timeSinceLastActivity > TWO_DAYS_IN_MS) {
    return {
      code: 'inactive',
      color: '#EF4444',
      label: 'Inactif depuis plus de 2 jours',
    };
  }

  return {
    code: 'not-today',
    color: '#F59E0B',
    label: 'Pas actif aujourd’hui',
  };
};

const ACTIONS = [
  { id: 'notes', label: 'Notes', Icon: EditNote },
  { id: 'fiche', label: 'Fiche descriptive', Icon: DescriptionOutlined },
  { id: 'courbes', label: 'Courbes de données', Icon: ShowChart },
  { id: 'carte', label: 'Localisation', Icon: PlaceOutlined },
];

const DEFAULT_THRESHOLDS = {
  temperature: [19, 35], CO2: [0, 1000], TVOC: [0, 500], sound: [0, 80],
  PM1_0: [0, 15], PM2_5: [0, 15], PM10: [0, 45], groundHumidity: [40, 80], humidity: [30, 65],
};

const MOBILE_SIDE_PAD = '22px';

const DashboardV2 = ({
  realTimeDataByTable = {},
  setSelectedDevice,
  setSelectedTable,
  stationProfiles = {},
  stationThresholds = {},
  alertValue = DEFAULT_THRESHOLDS,
  alertCount = 0,
  onOpenStationDetails,
  onToggleFavorite,
  onNavigateToProfile,
  onNavigateToAlerts,
  onOpenMenu,
  scrollToFavorites = false,
  onScrollToFavoritesDone,
  onOpenStationNotes,
  isDashboardLoading = false,
  hasDashboardLoadedOnce = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const favSectionRef = useRef(null);
  const { user } = useAuth();
  const [toast, setToast] = useState('');
  const [curvesDevice, setCurvesDevice] = useState(null);
  const [typeFilter, setTypeFilter] = useState('tous');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!scrollToFavorites || !favSectionRef.current) return;
    const timer = setTimeout(() => {
      favSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      onScrollToFavoritesDone?.();
    }, 150);
    return () => clearTimeout(timer);
  }, [scrollToFavorites, onScrollToFavoritesDone]);

  const firstName = user?.firstName || '';
  const lastName = user?.lastName || '';
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U';

  const isAlert = useCallback((val, threshold) => {
    const n = Number(val);

    if (!hasMetricValue(val) || !threshold) {
      return false;
    }

    let min = null;
    let max = null;

    if (Array.isArray(threshold)) {
      [min, max] = threshold;
    } else if (typeof threshold === 'object') {
      min = threshold.min;
      max = threshold.max;
    }

    const normalizedMin =
      min === null || min === undefined || min === ''
        ? null
        : Number(min);

    const normalizedMax =
      max === null || max === undefined || max === ''
        ? null
        : Number(max);

    if (
      normalizedMin !== null &&
      Number.isFinite(normalizedMin) &&
      n < normalizedMin
    ) {
      return true;
    }

    if (
      normalizedMax !== null &&
      Number.isFinite(normalizedMax) &&
      n > normalizedMax
    ) {
      return true;
    }

    return false;
  }, []);

  const favKeyOf = (table, id) => `${table}:${id}`;

  const isFav = (table, id) =>
    stationProfiles[favKeyOf(table, id)]?.isFavorite === true;

  const toggleFav = (table, id) => {
    onToggleFavorite?.({
      table,
      id,
    });
  };

  const rows = useMemo(() => {
    const tables = typeFilter === 'tous' ? ALL_TABLES : [typeFilter];
    const allRows = [];

    tables.forEach((table) => {
      const tableData = realTimeDataByTable[table] || {};
      const deviceIds = Object.keys(tableData);
      const mapper = new DeviceNameMapper(table, deviceIds);
      const metrics = getMetrics(table);

      deviceIds.forEach((id) => {
        const data = tableData[id] || {};
        const profile = stationProfiles[favKeyOf(table, id)] || {};
        const thresholds = stationThresholds[favKeyOf(table, id)] || {};

        const kpis = metrics.map((m) => {
          const value = getMetricValue(data, m.key);
          const threshold = thresholds[m.thr] || alertValue[m.thr];

          return {
            ...m,
            value,
            alert: isAlert(value, threshold),
          };
        });

        const visibleKpis = getVisibleKpis(kpis);

        allRows.push({
          id,
          table,
          name: String(profile.name || '').trim() || mapper.getAlias(id),
          data,

          /*
           * kpis contient toutes les mesures pour garder la logique d'alerte.
           * visibleKpis contient seulement les mesures réellement exploitables.
           */
          kpis,
          visibleKpis,

          /*
           * La station reste active si elle a un timestamp valide,
           * même si une ou plusieurs valeurs sont null.
           */
          activity: getActivityStatus(data.timestamp),

          isFavorite: profile.isFavorite === true,
          hasAlert: kpis.some((k) => k.alert),
          hasUsableMetric: hasAnyUsableMetric(kpis),
        });
      });
    });

    const q = searchQuery.trim().toLowerCase();
    if (!q) return allRows;
    return allRows.filter(
      (r) => r.name.toLowerCase().includes(q) || r.id.toLowerCase().includes(q) || r.table.toLowerCase().includes(q)
    );
  }, [
    realTimeDataByTable,
    typeFilter,
    isAlert,
    searchQuery,
    stationProfiles,
    stationThresholds,
    alertValue,
  ]);

  const alertRows = rows.filter((r) => r.hasAlert);
  const favRows = rows.filter((r) => r.isFavorite);
  const otherRows = rows;

  const handleAction = (actionId, row) => {
    setSelectedDevice?.(row.id);
    setSelectedTable?.(row.table);

    if (actionId === 'notes') {
      onOpenStationNotes?.(row);
      return;
    }

    if (actionId === 'fiche') {
      onOpenStationDetails?.(row);
      return;
    }

    if (actionId === 'courbes') {
      setCurvesDevice({
        id: row.id,
        table: row.table,
        name: row.name,
      });
      return;
    }

    const label =
      ACTIONS.find((a) => a.id === actionId)?.label || actionId;

    setToast(`${label} · ${row.name} (bientôt disponible)`);
  };

  const renderKpi = (k, compact = false) => {
    const Icon = k.Icon;
    const hasValue = hasMetricValue(k.value);

    return (
      <Box
        key={k.key}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          flex: compact ? undefined : '1 1 96px',
          minWidth: compact ? 0 : 96,
          p: compact ? '7px 8px' : '8px 10px',
          borderRadius: '10px',
          boxSizing: 'border-box',
          overflow: 'hidden',
          backgroundColor: k.alert ? 'rgba(255,87,34,0.13)' : C.input,
          border: k.alert ? `1.5px solid ${C.danger}` : '1.5px solid transparent',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: compact ? '4px' : '6px', minWidth: 0 }}>
          <Icon sx={{ fontSize: compact ? 12 : 14, color: k.alert ? C.danger : C.textMuted, flexShrink: 0 }} />
          <Typography
            sx={{
              fontSize: compact ? '9px' : '10px',
              color: k.alert ? C.danger : C.textPlaceholder,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {k.label}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '3px' }}>
          <Typography sx={{ fontSize: '15px', fontWeight: 700, color: k.alert ? C.danger : C.textPrimary, lineHeight: 1 }}>
            {formatValue(k.value)}
          </Typography>
          {hasValue && (
            <Typography sx={{ fontSize: '10px', color: k.alert ? C.danger : C.textMuted, mb: '1px' }}>
              {k.unit}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  const typePill = (table, compact = false) => {
    const typeMeta = TYPE_META[table] || TYPE_META.climatrack;
    return (
      <Box
        sx={{
          display: 'inline-flex',
          borderRadius: '999px',
          p: compact ? '4px 10px' : '5px 12px',
          flexShrink: 0,
          maxWidth: compact ? 96 : undefined,
          backgroundColor: typeMeta.tint,
          border: `1px solid ${typeMeta.border}`,
        }}
      >
        <Typography
          sx={{
            fontSize: compact ? '11px' : '12px',
            fontWeight: 700,
            color: typeMeta.color,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {table}
        </Typography>
      </Box>
    );
  };

  const renderDeviceHead = (row) => {
    const typeMeta = TYPE_META[row.table] || TYPE_META.climatrack;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%' }}>
        <Box
          component="button"
          type="button"
          onClick={() => toggleFav(row.table, row.id)}
          title={isFav(row.table, row.id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
          sx={{ background: 'none', border: 'none', p: 0, cursor: 'pointer', display: 'flex', flexShrink: 0 }}
        >
          {isFav(row.table, row.id) ? (
            <Star sx={{ fontSize: 18, color: '#F59E0B' }} />
          ) : (
            <StarBorder sx={{ fontSize: 18, color: C.textPlaceholder }} />
          )}
        </Box>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '10px',
            flexShrink: 0,
            backgroundColor: typeMeta.tint,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Memory sx={{ fontSize: 18, color: typeMeta.color }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
            <Box
              title={row.activity?.label}
              sx={{
                width: 8,
                height: 8,
                borderRadius: '999px',
                flexShrink: 0,
                backgroundColor: row.activity?.color || '#EF4444',
                boxShadow: `0 0 8px ${row.activity?.color || '#EF4444'}`,
              }}
            />
            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: 700,
                color: C.textPrimary,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {row.name}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: '11px', color: C.textPlaceholder }}>#{row.id}</Typography>
        </Box>
        {isMobile && typePill(row.table, true)}
      </Box>
    );
  };

  const renderActions = (row) => {
    const typeMeta = TYPE_META[row.table] || TYPE_META.climatrack;
    return (
      <Box
        sx={{
          display: 'flex',
          gap: '8px',
          flexShrink: 0,
          justifyContent: { xs: 'center', md: 'flex-end' },
          width: { xs: '100%', md: 168 },
        }}
      >
        {ACTIONS.map((a) => {
          const Icon = a.Icon;
          return (
            <Box
              key={a.id}
              component="button"
              type="button"
              onClick={() => handleAction(a.id, row)}
              title={a.label}
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                cursor: 'pointer',
                backgroundColor: C.input,
                border: `1px solid ${C.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: C.textMuted,
                transition: 'all 0.15s ease',
                '&:hover': { color: C.textPrimary, borderColor: typeMeta.color },
              }}
            >
              <Icon sx={{ fontSize: 16 }} />
            </Box>
          );
        })}
      </Box>
    );
  };
  const renderNoUsableMetric = () => (
    <Box
      sx={{
        width: '100%',
        minHeight: 40,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '10px',
        backgroundColor: C.input,
        border: `1px dashed ${C.border}`,
        color: C.textPlaceholder,
        fontSize: '12px',
        fontWeight: 600,
      }}
    >
      Aucune valeur exploitable
    </Box>
  );

  const renderMobileCard = (row) => (
    <Box
      key={`${row.table}-${row.id}`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        p: '14px',
        borderRadius: '14px',
        backgroundColor: C.card,
        border: `1px solid ${C.border}`,
        boxSizing: 'border-box',
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      {renderDeviceHead(row)}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: '8px',
          width: '100%',
          minWidth: 0,
        }}
      >
        {row.visibleKpis.length > 0
          ? row.visibleKpis.map((k) => renderKpi(k, true))
          : renderNoUsableMetric()}
      </Box>
      {renderActions(row)}
    </Box>
  );

  const renderRow = (row) => {
    if (isMobile) return renderMobileCard(row);

    const typeMeta = TYPE_META[row.table] || TYPE_META.climatrack;
    return (
      <Box
        key={`${row.table}-${row.id}`}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '16px',
          p: '16px',
          borderRadius: '14px',
          backgroundColor: C.card,
          border: `1px solid ${C.border}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', width: 210, flexShrink: 0 }}>
          <Box
            component="button"
            type="button"
            onClick={() => toggleFav(row.table, row.id)}
            title={isFav(row.table, row.id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
            sx={{ background: 'none', border: 'none', p: 0, cursor: 'pointer', display: 'flex' }}
          >
            {isFav(row.table, row.id) ? (
              <Star sx={{ fontSize: 18, color: '#F59E0B' }} />
            ) : (
              <StarBorder sx={{ fontSize: 18, color: C.textPlaceholder }} />
            )}
          </Box>
          <Box
            sx={{
              width: 40, height: 40, borderRadius: '10px', flexShrink: 0,
              backgroundColor: typeMeta.tint, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Memory sx={{ fontSize: 18, color: typeMeta.color }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
              <Box
                title={row.activity?.label}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '999px',
                  flexShrink: 0,
                  backgroundColor: row.activity?.color || '#EF4444',
                  boxShadow: `0 0 8px ${row.activity?.color || '#EF4444'}`,
                }}
              />
              <Typography sx={{ fontSize: '13px', fontWeight: 700, color: C.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {row.name}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '11px', color: C.textPlaceholder }}>#{row.id}</Typography>
          </Box>
        </Box>

        <Box sx={{ width: 120, flexShrink: 0 }}>
          {typePill(row.table)}
        </Box>

        <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
          {row.visibleKpis.length > 0
            ? row.visibleKpis.map((k) => renderKpi(k))
            : renderNoUsableMetric()}
        </Box>

        {renderActions(row)}
      </Box>
    );
  };

  const renderSection = (label, Icon, color, list, sectionRef) => {
    if (list.length === 0) return null;
    return (
      <Box ref={sectionRef} sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', px: '4px', pt: '4px' }}>
          <Icon sx={{ fontSize: 15, color }} />
          <Typography sx={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.8px', color }}>
            {label}
          </Typography>
          <Box sx={{ borderRadius: '999px', px: '8px', py: '2px', backgroundColor: C.input }}>
            <Typography sx={{ fontSize: '11px', fontWeight: 600, color: C.textMuted }}>{list.length}</Typography>
          </Box>
        </Box>
        {list.map(renderRow)}
      </Box>
    );
  };

  const iconBtnSx = {
    width: 36,
    height: 36,
    borderRadius: '10px',
    cursor: 'pointer',
    backgroundColor: C.input,
    border: `1px solid ${C.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: C.textMuted,
    transition: 'all 0.15s ease',
    '&:hover': { color: C.textPrimary, borderColor: C.textMuted },
  };

  const menuBtnSx = {
    width: 36,
    height: 36,
    borderRadius: '10px',
    cursor: 'pointer',
    backgroundColor: 'rgba(255,255,255,0.07)',
    border: `1px solid ${C.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: C.textPrimary,
    flexShrink: 0,
  };

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: fontStack,
        boxSizing: 'border-box',
        mx: { xs: 0, md: '-30px' },
        mt: { xs: 0, md: '-30px' },
      }}
    >
      {/* Barre supérieure */}
      <Box
        sx={{
          width: '100%',
          height: { xs: 56, md: 64 },
          px: { xs: MOBILE_SIDE_PAD, md: '28px' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          backgroundColor: C.topbar,
          borderBottom: `1px solid ${C.border}`,
          flexWrap: 'nowrap',
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 1, minWidth: 0, overflow: 'hidden' }}>
          {isMobile && (
            <Box
              component="button"
              type="button"
              onClick={onOpenMenu}
              aria-label="Ouvrir le menu"
              sx={menuBtnSx}
            >
              <Menu sx={{ fontSize: 18 }} />
            </Box>
          )}
          <Typography
            sx={{
              fontSize: { xs: '15px', md: '18px' },
              fontWeight: 700,
              color: C.textPrimary,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
            }}
          >
            Tableau de bord
          </Typography>
          <Typography sx={{ display: { xs: 'none', sm: 'block' }, fontSize: { xs: '13px', md: '14px' }, color: C.textPlaceholder, flexShrink: 0 }}>/</Typography>
          <Typography
            sx={{
              display: { xs: 'none', sm: 'block' },
              fontSize: { xs: '12px', md: '13px' },
              color: C.textMuted,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            Supervision
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: '10px', md: '12px' }, flexShrink: 0, pr: { xs: '2px', md: 0 } }}>
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              gap: '10px',
              width: { sm: 220, md: 280 },
              height: 38,
              px: '14px',
              borderRadius: '10px',
              backgroundColor: C.input,
              border: `1px solid ${C.border}`,
            }}
          >
            <Search sx={{ fontSize: 14, color: C.textPlaceholder }} />
            <InputBase
              placeholder="Rechercher un boitier..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                flex: 1,
                fontSize: '12px',
                color: C.textPrimary,
                '& input::placeholder': { color: C.textPlaceholder, opacity: 1 },
              }}
            />
          </Box>

          <Box
            component="button"
            type="button"
            onClick={onNavigateToAlerts}
            title="Notifications"
            sx={iconBtnSx}
          >
            <Badge
              badgeContent={alertCount}
              color="error"
              max={99}
              invisible={!alertCount}
              sx={{ '& .MuiBadge-badge': { fontSize: '10px', height: 16, minWidth: 16 } }}
            >
              <NotificationsNone sx={{ fontSize: 16 }} />
            </Badge>
          </Box>

          <Box
            component="button"
            type="button"
            onClick={onNavigateToProfile}
            title="Mon compte"
            sx={{
              width: 36,
              height: 36,
              minWidth: 36,
              borderRadius: '50%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              background: avatarGradient,
              border: 'none',
              color: '#0B1320',
              fontSize: '13px',
              fontWeight: 700,
              transition: 'opacity 0.15s ease',
              '&:hover': { opacity: 0.9, color: '#0B1320' },
            }}
          >
            {initials}
          </Box>
        </Box>
      </Box>

      {/* Recherche mobile */}
      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          alignItems: 'center',
          gap: '10px',
          mx: MOBILE_SIDE_PAD,
          mt: '14px',
          height: 44,
          px: '14px',
          borderRadius: '10px',
          backgroundColor: C.input,
          border: `1px solid ${C.border}`,
          boxSizing: 'border-box',
        }}
      >
        <Search sx={{ fontSize: 14, color: C.textPlaceholder }} />
        <InputBase
          placeholder="Rechercher un boitier..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            flex: 1,
            fontSize: '12px',
            color: C.textPrimary,
            '& input::placeholder': { color: C.textPlaceholder, opacity: 1 },
          }}
        />
      </Box>

      {/* Contenu principal */}
      <Box
        sx={{
          width: '100%',
          maxWidth: 1200,
          mx: 'auto',
          px: { xs: MOBILE_SIDE_PAD, md: '0' },
          py: { xs: '14px', md: '20px' },
          pb: { xs: '24px', md: '20px' },
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: '14px', md: '16px' },
          boxSizing: 'border-box',
          minWidth: 0,
        }}
      >
        {/* Sous-titre + filtres */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          <Typography sx={{ fontSize: { xs: '18px', md: '20px' }, fontWeight: 700, color: C.textPrimary }}>
            Vue par boitier
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: '8px',
              flexWrap: { xs: 'nowrap', md: 'wrap' },
              overflowX: { xs: 'auto', md: 'visible' },
              pb: { xs: '4px', md: 0 },
              mx: { xs: '-4px', md: 0 },
              px: { xs: '4px', md: 0 },
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {FILTER_OPTIONS.map((t) => {
              const on = t === typeFilter;

              return (
                <Box
                  key={t}
                  component="button"
                  type="button"
                  onClick={() => setTypeFilter(t)}
                  sx={{
                    borderRadius: '999px',
                    p: '8px 14px',
                    cursor: 'pointer',
                    flexShrink: 0,
                    backgroundColor: on
                      ? 'rgba(52, 211, 153, 0.13)'
                      : C.input,
                    border: `1px solid ${on ? 'rgba(52, 211, 153, 0.75)' : C.border
                      }`,
                    color: on ? '#34D399' : C.textMuted,
                    fontSize: '12px',
                    fontWeight: on ? 700 : 600,
                    transition: 'all 0.2s ease',

                    '&:hover': {
                      backgroundColor: on
                        ? 'rgba(52, 211, 153, 0.18)'
                        : 'rgba(255, 255, 255, 0.06)',
                      borderColor: on
                        ? '#34D399'
                        : 'rgba(255, 255, 255, 0.18)',
                      color: on ? '#34D399' : C.textPrimary,
                    },
                  }}
                >
                  {t}
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* En-têtes de colonnes (desktop) */}
        {rows.length > 0 && (
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              gap: '16px',
              px: '16px',
            }}
          >
            <Typography sx={{ width: 210, flexShrink: 0, fontSize: '11px', fontWeight: 700, letterSpacing: '0.8px', color: C.textPlaceholder }}>
              BOITIER
            </Typography>
            <Typography sx={{ width: 120, flexShrink: 0, fontSize: '11px', fontWeight: 700, letterSpacing: '0.8px', color: C.textPlaceholder }}>
              TYPE
            </Typography>
            <Typography sx={{ flex: 1, fontSize: '11px', fontWeight: 700, letterSpacing: '0.8px', color: C.textPlaceholder }}>
              INDICATEURS
            </Typography>
            <Typography sx={{ width: 168, flexShrink: 0, textAlign: 'right', fontSize: '11px', fontWeight: 700, letterSpacing: '0.8px', color: C.textPlaceholder }}>
              ACTIONS
            </Typography>
          </Box>
        )}

        {!hasDashboardLoadedOnce ? (
          <Box
            sx={{
              p: '40px',
              textAlign: 'center',
              borderRadius: '14px',
              backgroundColor: C.card,
              border: `1px solid ${C.border}`,
            }}
          >
            <Typography
              sx={{
                color: C.textMuted,
                fontWeight: 700,
              }}
            >
              Chargement des boîtiers...
            </Typography>

            <Typography
              sx={{
                marginTop: '6px',
                color: C.textPlaceholder,
                fontSize: '12px',
              }}
            >
              Récupération des dernières valeurs reçues.
            </Typography>
          </Box>
        ) : rows.length === 0 ? (
          <Box
            sx={{
              p: '40px',
              textAlign: 'center',
              borderRadius: '14px',
              backgroundColor: C.card,
              border: `1px solid ${C.border}`,
            }}
          >
            <Typography sx={{ color: C.textMuted }}>
              {searchQuery
                ? 'Aucun boitier ne correspond à votre recherche.'
                : 'Aucun boitier disponible pour ce type.'}
            </Typography>
          </Box>
        ) : (
          <>
            {renderSection('EN ALERTE', WarningAmberRounded, C.danger, alertRows)}
            {renderSection('FAVORIS', Star, '#F59E0B', favRows, favSectionRef)}
            {renderSection('AUTRES BOITIERS', Layers, C.textMuted, otherRows)}
          </>
        )}
      </Box>

      <Snackbar
        open={!!toast}
        autoHideDuration={2500}
        onClose={() => setToast('')}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      <DeviceCurvesModal
        open={!!curvesDevice}
        onClose={() => setCurvesDevice(null)}
        device={curvesDevice}
      />
    </Box>
  );
};

export default DashboardV2;
